import type { Application } from "@hotwired/stimulus";

export interface GuidedSurface {
  selector?: string;
  highlight?: string;
  expand?: string;
  frame?: string;
  frame_page?: string;
  frame_trigger?: string;
  open?: string;
}

// Opens the same controls users operate; never fabricates a frame response.
export class WalkthroughSurface {
  private frame: HTMLElement | null = null;
  private frameSource: string | undefined;
  private pending: AbortController | null = null;
  private expanded: HTMLElement | null = null;
  private openedCheckbox: HTMLInputElement | null = null;

  constructor(private application: Application) {}

  async prepare(step: GuidedSurface) {
    this.pending?.abort();
    this.pending = new AbortController();
    const signal = this.pending.signal;
    await this.prepareFrame(step, signal);
    if (step.open && (!step.selector || !this.target(step.selector))) await this.clickControl(step.open, signal);
    if (step.expand) {
      const element = document.querySelector<HTMLElement>(step.expand);
      if (element instanceof HTMLInputElement && element.type === "checkbox") {
        if (!element.checked) {
          element.click();
          this.openedCheckbox = element;
        }
      } else if (element && !element.classList.contains("collapse-open")) {
        const control = element.querySelector<HTMLElement>('[data-action~="click->expanding#toggleExpand"]');
        if (!control) throw new Error("Guide expander unavailable");
        control.click();
        this.expanded = control;
      }
    }
    if (step.selector) await this.waitForLayout(step.selector, signal);
  }

  target(selector: string): HTMLElement | null {
    return Array.from(document.querySelectorAll<HTMLElement>(selector))
      .find(element => {
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && element.checkVisibility({ visibilityProperty: true, opacityProperty: true });
      }) || null;
  }

  close() {
    this.pending?.abort();
    this.closeFrame();
    if (this.expanded?.closest(".collapse-open")) this.expanded.click();
    this.expanded = null;
    if (this.openedCheckbox?.checked) this.openedCheckbox.click();
    this.openedCheckbox = null;
  }

  private async prepareFrame(step: GuidedSurface, signal: AbortSignal) {
    const source = step.frame_trigger || step.frame_page;
    if (this.frameSource !== source) this.closeFrame();
    if (step.frame && source && !this.frame) {
      const frame = document.getElementById(step.frame);
      if (!frame) throw new Error("Guide frame unavailable");
      this.frame = frame;
      this.frameSource = source;
      frame.removeAttribute("complete");
      const loaded = this.waitFor(() => frame.hasAttribute("complete"), signal);
      try {
        await this.clickControl(step.frame_trigger || `a[href="${step.frame_page}"]`, signal);
      } catch (error) {
        this.pending?.abort();
        await loaded.catch(() => {});
        throw error;
      }
      await loaded;
    }
  }

  private async clickControl(selector: string, signal: AbortSignal) {
    let control = this.target(selector);
    if (!control) {
      for (const candidate of document.querySelectorAll<HTMLElement>(selector)) {
        const menu = candidate.closest<HTMLElement>(".dropdown");
        const toggle = Array.from(menu?.querySelectorAll<HTMLElement>(":scope > button, :scope > label, :scope > [role='button']") || [])
          .find(button => button.checkVisibility({ visibilityProperty: true, opacityProperty: true }));
        if (!toggle) continue;
        toggle.focus();
        toggle.click();
        await this.waitFor(() => Boolean(this.target(selector)), signal);
        control = this.target(selector);
        break;
      }
    }
    if (!control) throw new Error(`Guide control unavailable: ${selector}`);
    control.scrollIntoView({ block: "center" });
    control.focus();
    control.click();
  }

  private closeFrame() {
    if (!this.frame) return;
    const identifier = this.frame.id === "dynamic_modal_frame" ? "dynamic-modal" : "modal";
    const root = this.frame.closest<HTMLElement>(`[data-controller~="${identifier}"]`);
    if (root) {
      const controller = this.application.getControllerForElementAndIdentifier(root, identifier) as { close?: () => void } | null;
      controller?.close?.();
    }
    this.frame = null;
    this.frameSource = undefined;
  }

  private waitForLayout(selector: string, signal: AbortSignal) {
    let previous = "";
    let changedAt = performance.now();
    return this.waitFor(() => {
      const target = this.target(selector);
      if (!target) return false;
      const { x, y, width, height } = target.getBoundingClientRect();
      const bounds = [x, y, width, height].map(value => Math.round(value)).join(",");
      if (bounds !== previous) {
        previous = bounds;
        changedAt = performance.now();
      }
      // A frame can be complete while its drawer or expander is still moving.
      for (let node: HTMLElement | null = target; node; node = node.parentElement) {
        if (node.getAnimations?.().some(animation => animation.playState === "running" &&
          animation.effect?.getComputedTiming().iterations !== Infinity)) return false;
      }
      return performance.now() - changedAt >= 150;
    }, signal);
  }

  private waitFor(ready: () => boolean, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const check = () => { if (ready()) finish(); };
      const observer = new MutationObserver(check);
      const timeout = window.setTimeout(() => finish(new Error("Guide surface timed out")), 10000);
      const interval = window.setInterval(check, 100);
      const abort = () => finish(new Error("Guide navigation cancelled"));
      const finish = (error?: Error) => {
        observer.disconnect();
        window.clearTimeout(timeout);
        window.clearInterval(interval);
        signal.removeEventListener("abort", abort);
        error ? reject(error) : resolve();
      };
      signal.addEventListener("abort", abort, { once: true });
      observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
      // Let the clicked control start its request before inspecting `complete`.
      queueMicrotask(check);
    });
  }
}
